import type { FicheTabId } from "@/components/biens/ficheTabs";

/**
 * Onglets/blocs de la fiche location masquables via un réglage explicite
 * (rôle réel ou préréglage popup). Distinct des champs individuels, qui
 * passent par field_permissions/F() sans intermédiaire.
 */
export type BienDetailHideableSection = "locataires" | "etatDesLieux" | "finalisationSrs";

/** Clé synthétique field_key pour un onglet entier, réutilisant field_permissions. */
export function tabStructuralKey(id: FicheTabId): string {
  return `__tab_${id}`;
}

/** Clé synthétique field_key pour un bloc/section, réutilisant field_permissions. */
export function sectionStructuralKey(key: BienDetailHideableSection): string {
  return `__section_${key}`;
}

/**
 * Filtre les onglets visibles pour un rôle réel + préréglage optionnel.
 *
 * `canSeeStructural` répond pour `tabStructuralKey(id)` en tenant compte du
 * rôle réel (déjà mergé avec le défaut "visible si aucune ligne trouvée" —
 * cf. useFieldPermissions). `presetVisible` fait de même pour le préréglage
 * actif ; `null` si aucun préréglage n'est actif — dans ce cas seul le rôle
 * réel décide.
 *
 * `alwaysIncluded` sert au pseudo-onglet "permissions" (admin) : jamais
 * soumis à cette logique, toujours gardé s'il était dans `ids`.
 */
export function visibleFicheTabs(
  ids: FicheTabId[],
  canSeeStructural: (key: string) => boolean,
  presetVisible: ((key: string) => boolean) | null,
  alwaysIncluded: FicheTabId[] = [],
): FicheTabId[] {
  return ids.filter((id) => {
    if (alwaysIncluded.includes(id)) return true;
    const key = tabStructuralKey(id);
    if (!canSeeStructural(key)) return false;
    if (presetVisible && !presetVisible(key)) return false;
    return true;
  });
}

/**
 * Onglet de repli quand l'onglet actif vient de disparaître de la liste
 * visible (changement de rôle simulé, ou préréglage qui vient de s'activer).
 * `null` si plus aucun onglet n'est visible — l'appelant doit alors afficher
 * un état de repli plutôt qu'une fiche sans navigation.
 */
export function fallbackActiveTab(visible: FicheTabId[], current: FicheTabId): FicheTabId | null {
  if (visible.includes(current)) return current;
  return visible[0] ?? null;
}

/**
 * Visibilité effective d'un bloc à l'intérieur d'un onglet (ex: "Clôture
 * SRS" dans "Cycle en cours"). Trois sources de masquage indépendantes,
 * dans cet ordre de priorité :
 *  1. Mode réglage des droits : toujours visible, l'admin règle tout.
 *  2. `hideSections` statique — usage historique /publi-temp, inconditionnel.
 *  3. Rôle réel, puis préréglage (ne peut que restreindre davantage).
 */
export function isSectionVisible(
  key: BienDetailHideableSection,
  opts: {
    permsMode: boolean;
    hideSections?: BienDetailHideableSection[];
    canSeeStructural: (key: string) => boolean;
    presetVisible: ((key: string) => boolean) | null;
  },
): boolean {
  if (opts.permsMode) return true;
  if ((opts.hideSections ?? []).includes(key)) return false;
  const structKey = sectionStructuralKey(key);
  if (!opts.canSeeStructural(structKey)) return false;
  if (opts.presetVisible && !opts.presetVisible(structKey)) return false;
  return true;
}

/**
 * Visibilité effective d'un CHAMP INDIVIDUEL (pas un onglet ni un bloc) —
 * ex: "Renon reçu le", "ELS requis", dans le préréglage "Apartments for
 * Rent". C'est la fonction que F() appelle pour chaque champ de la fiche.
 *
 * `canSeeField` est déjà le résultat de `useFieldPermissions(role).canSee`
 * pour ce champ (rôle réel). `presetVisible` est `null` hors préréglage —
 * dans ce cas seul le rôle réel décide, comportement strictement identique
 * à avant ce chantier.
 */
export function isFieldVisible(
  key: string,
  canSeeField: (key: string) => boolean,
  presetVisible: ((key: string) => boolean) | null,
): boolean {
  if (!canSeeField(key)) return false;
  if (presetVisible && !presetVisible(key)) return false;
  return true;
}
